import { useState, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, ShieldCheck, Check, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

// Client-side mirror of the backend policy (must match commissions/security.py).
function checkPolicy(pw) {
  return {
    length: (pw || "").length >= 10,
    lower: /[a-z]/.test(pw || ""),
    upper: /[A-Z]/.test(pw || ""),
    digit: /\d/.test(pw || ""),
  };
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const policy = useMemo(() => checkPolicy(pw), [pw]);
  const allGood = policy.length && policy.lower && policy.upper && policy.digit;
  const matches = pw && pw === pw2;

  const missingToken = !token || token.length < 10;

  async function submit(e) {
    e.preventDefault();
    if (!allGood) {
      toast.error("Password doesn't meet the requirements yet.");
      return;
    }
    if (!matches) {
      toast.error("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password updated. You can sign in with your new password.");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-950 grid lg:grid-cols-5">
      <div className="lg:col-span-3 flex flex-col p-8 lg:p-16">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-50 transition-colors w-fit" data-testid="back-to-login-link">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>

        <div className="flex-1 flex items-center max-w-md w-full mx-auto">
          <div className="w-full">
            <div className="mb-10">
              <div className="inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/5 px-3 py-1 text-xs text-cyan-300 mb-4">
                <ShieldCheck className="w-3.5 h-3.5" /> Secure password reset
              </div>
              <h1 className="font-display text-4xl tracking-tight font-medium mb-2">Choose a new password</h1>
              <p className="text-slate-400">Pick something you haven&apos;t used elsewhere. We&apos;ll update it right away.</p>
            </div>

            {missingToken ? (
              <div data-testid="reset-password-missing-token" className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-6 text-sm text-amber-200">
                <div className="font-semibold text-amber-100 mb-1">This link is missing its token.</div>
                Open the reset email again and click the link, or{" "}
                <Link to="/forgot-password" className="underline underline-offset-2 hover:text-amber-100">
                  request a new one
                </Link>.
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 block">New password</label>
                  <input
                    data-testid="reset-password-new-input"
                    type="password"
                    required
                    autoFocus
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    className="w-full rounded-lg border border-slate-800 bg-slate-900 px-4 py-3 text-slate-50 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-colors font-mono"
                    placeholder="••••••••••"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 block">Confirm new password</label>
                  <input
                    data-testid="reset-password-confirm-input"
                    type="password"
                    required
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    className="w-full rounded-lg border border-slate-800 bg-slate-900 px-4 py-3 text-slate-50 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-colors font-mono"
                    placeholder="••••••••••"
                  />
                </div>

                <div className="rounded-lg border border-slate-800 bg-slate-900/50 p-4 text-sm space-y-1.5" data-testid="reset-password-policy">
                  <PolicyRow ok={policy.length} label="At least 10 characters" />
                  <PolicyRow ok={policy.lower} label="A lower-case letter" />
                  <PolicyRow ok={policy.upper} label="An upper-case letter" />
                  <PolicyRow ok={policy.digit} label="A number" />
                  <PolicyRow ok={!!matches} label="Both entries match" />
                </div>

                <button
                  data-testid="reset-password-submit-button"
                  type="submit"
                  disabled={busy || !allGood || !matches}
                  className="w-full inline-flex items-center justify-center gap-2 bg-brand-gradient text-slate-950 disabled:opacity-60 disabled:cursor-not-allowed px-6 py-3.5 rounded-full font-semibold text-sm transition-all hover:-translate-y-[1px] hover:brightness-110"
                >
                  {busy ? "Saving…" : "Update password"} {!busy && <ArrowRight className="w-4 h-4" />}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      <div className="hidden lg:block lg:col-span-2 relative overflow-hidden bg-[#040814]">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-transparent to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div>
            <img src="/brand/steele-cloud-long.png" alt="Steele Cloud" className="w-full max-w-[320px] mb-10 -ml-2" />
            <h2 className="font-display text-3xl tracking-tight font-medium leading-tight">
              One-time link.<br/>Single-use token.<br/>Hash never leaves the vault.
            </h2>
          </div>
        </div>
      </div>
    </div>
  );
}

function PolicyRow({ ok, label }) {
  return (
    <div className={`flex items-center gap-2 ${ok ? "text-emerald-300" : "text-slate-500"}`}>
      {ok ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
      <span>{label}</span>
    </div>
  );
}
